import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { harvestService } from '../../services/harvestService';
import toast from 'react-hot-toast';

interface HarvestClient {
  id: string;
  name: string;
  is_active: boolean;
  address?: string;
  currency: string;
}

interface HarvestClientImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (client: HarvestClient) => void;
}

export function HarvestClientImportModal({ isOpen, onClose, onImport }: HarvestClientImportModalProps) {
  const [clients, setClients] = useState<HarvestClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<HarvestClient | null>(null);
  
  useEffect(() => {
    if (isOpen) {
      loadClients();
    }
  }, [isOpen]);
  
  const loadClients = async () => {
    setLoading(true);
    const toastId = toast.loading('Loading clients from Harvest...');
    
    try {
      const connection = await harvestService.getConnection();
      if (!connection) {
        toast.error('Not connected to Harvest', { id: toastId });
        onClose();
        return;
      }
      
      const response = await harvestService.listClients();
      setClients(response.clients || []);
      toast.success(`Loaded ${response.clients?.length || 0} clients from Harvest`, { id: toastId });
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients from Harvest', { id: toastId });
    } finally {
      setLoading(false);
    }
  };
  
  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleImport = () => {
    if (selectedClient) {
      onImport(selectedClient);
      onClose();
    }
  };
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-lg shadow-xl">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              Import Client from Harvest
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          <div className="p-6">
            {/* Search */}
            <div className="relative mb-4">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            {/* Client List */}
            <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">
                  Loading clients...
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <BuildingOfficeIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  No clients found
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredClients.map((client) => (
                    <div
                      key={client.id}
                      onClick={() => setSelectedClient(client)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition ${
                        selectedClient?.id === client.id ? 'bg-primary-50 border-l-4 border-primary-600' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900">{client.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className={`px-2 py-1 text-xs rounded ${
                                client.is_active
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {client.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <span className="text-xs text-gray-500">
                              Currency: {client.currency}
                            </span>
                          </div>
                          {client.address && (
                            <p className="text-sm text-gray-600 mt-1">{client.address}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={!selectedClient}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Import Selected Client
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

